import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Recycle, Droplets, Sun, Wind, ChevronLeft, ChevronRight, Play, Pause, X, Maximize2, Volume2, VolumeX } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Lightbox Modal Component
const MediaLightbox = ({ media, currentIndex, onClose, onNext, onPrevious }) => {
  const [isMuted, setIsMuted] = useState(true);
  const currentMedia = media[currentIndex];

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') onPrevious();
      if (e.key === 'ArrowRight') onNext();
    };

    window.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [onClose, onNext, onPrevious]);

  return (
    <div className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center">
      {/* Close Button */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 p-3 bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors z-50"
      >
        <X className="h-6 w-6" />
      </button>

      {/* Navigation - Previous */}
      {media.length > 1 && (
        <button
          onClick={onPrevious}
          className="absolute left-4 top-1/2 -translate-y-1/2 p-3 bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors z-50"
        >
          <ChevronLeft className="h-8 w-8" />
        </button>
      )}

      {/* Media Content */}
      <div className="max-w-6xl max-h-[90vh] w-full mx-4">
        {currentMedia.type === 'video' ? (
          <div className="relative">
            <video
              src={currentMedia.url}
              className="w-full max-h-[80vh] object-contain"
              controls
              autoPlay
              muted={isMuted}
            />
            <button
              onClick={() => setIsMuted(!isMuted)}
              className="absolute bottom-4 right-4 p-2 bg-black/50 hover:bg-black/70 rounded-full text-white transition-colors"
            >
              {isMuted ? <VolumeX className="h-5 w-5" /> : <Volume2 className="h-5 w-5" />}
            </button>
          </div>
        ) : (
          <img
            src={currentMedia.url}
            alt={currentMedia.caption || 'Ecosystem media'}
            className="w-full max-h-[80vh] object-contain"
          />
        )}

        {/* Caption */}
        {currentMedia.caption && (
          <div className="text-center mt-4">
            <p className="text-white text-lg">{currentMedia.caption}</p>
          </div>
        )}

        {/* Counter */}
        <div className="text-center mt-2">
          <p className="text-white/60 text-sm">{currentIndex + 1} of {media.length}</p>
        </div>
      </div>

      {/* Navigation - Next */}
      {media.length > 1 && (
        <button
          onClick={onNext}
          className="absolute right-4 top-1/2 -translate-y-1/2 p-3 bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors z-50"
        >
          <ChevronRight className="h-8 w-8" />
        </button>
      )}

      {/* Thumbnail Strip */}
      {media.length > 1 && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 p-2 bg-black/50 rounded-lg max-w-[90vw] overflow-x-auto">
          {media.map((item, index) => (
            <button
              key={item.id || index}
              onClick={() => {
                const diff = index - currentIndex;
                if (diff > 0) {
                  for (let i = 0; i < diff; i++) onNext();
                } else if (diff < 0) {
                  for (let i = 0; i < Math.abs(diff); i++) onPrevious();
                }
              }}
              className={`flex-shrink-0 w-16 h-12 rounded overflow-hidden border-2 transition-all ${
                index === currentIndex ? 'border-white scale-110' : 'border-transparent opacity-60 hover:opacity-100'
              }`}
            >
              {item.type === 'video' ? (
                <div className="w-full h-full bg-gray-800 flex items-center justify-center">
                  <Play className="h-4 w-4 text-white" />
                </div>
              ) : (
                <img src={item.url} alt="" className="w-full h-full object-cover" />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Ecosystem Section Carousel Component
const EcosystemCarousel = ({ section }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [showLightbox, setShowLightbox] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const media = section.media || [];

  useEffect(() => {
    if (!isPlaying || media.length <= 1) return;
    
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % media.length);
    }, 4000);

    return () => clearInterval(interval);
  }, [isPlaying, media.length]);

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev - 1 + media.length) % media.length);
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev + 1) % media.length);
  };

  const openLightbox = (index) => {
    setLightboxIndex(index);
    setShowLightbox(true);
    setIsPlaying(false);
  };

  const closeLightbox = () => {
    setShowLightbox(false);
  };

  const lightboxPrevious = () => {
    setLightboxIndex((prev) => (prev - 1 + media.length) % media.length);
  };

  const lightboxNext = () => {
    setLightboxIndex((prev) => (prev + 1) % media.length);
  };

  const getColorGradient = (scheme) => {
    const gradients = {
      green: 'from-green-50 to-emerald-50',
      amber: 'from-amber-50 to-yellow-50',
      teal: 'from-green-50 to-teal-50',
      blue: 'from-blue-50 to-cyan-50'
    };
    return gradients[scheme] || gradients.green;
  };

  if (media.length === 0) {
    return (
      <div className="text-center">
        <div className="mb-6 rounded-2xl overflow-hidden shadow-2xl bg-gray-100 h-80 flex items-center justify-center">
          <p className="text-gray-500">No media available</p>
        </div>
      </div>
    );
  }

  const currentMedia = media[currentIndex];

  return (
    <div className="text-center">
      {/* Media Display */}
      <div className="mb-6 rounded-2xl overflow-hidden shadow-2xl relative group h-80">
        {currentMedia.type === 'video' ? (
          <div className="relative w-full h-full">
            <video
              src={currentMedia.url}
              className="w-full h-full object-cover"
              autoPlay
              loop
              muted
              playsInline
              onClick={() => openLightbox(currentIndex)}
            />
            {/* Play button overlay for videos */}
            <div 
              onClick={() => openLightbox(currentIndex)}
              className="absolute inset-0 flex items-center justify-center cursor-pointer bg-black/20 hover:bg-black/30 transition-colors"
            >
              <div className="w-16 h-16 bg-white/90 rounded-full flex items-center justify-center shadow-lg hover:scale-110 transition-transform">
                <Play className="h-8 w-8 text-green-600 ml-1" />
              </div>
            </div>
          </div>
        ) : (
          <img 
            src={currentMedia.url}
            alt={currentMedia.caption || section.title}
            className="w-full h-full object-cover hover:scale-105 transition-transform duration-500 cursor-pointer"
            onClick={() => openLightbox(currentIndex)}
          />
        )}

        {/* Enlarge Icon */}
        <button
          onClick={() => openLightbox(currentIndex)}
          className="absolute top-3 left-3 bg-black/50 hover:bg-black/70 p-2 rounded-full text-white opacity-0 group-hover:opacity-100 transition-opacity"
          title="Click to enlarge"
        >
          <Maximize2 className="h-4 w-4" />
        </button>

        {/* Navigation Controls */}
        {media.length > 1 && (
          <>
            <button
              onClick={(e) => { e.stopPropagation(); goToPrevious(); }}
              className="absolute left-3 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white p-2 rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); goToNext(); }}
              className="absolute right-3 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white p-2 rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <ChevronRight className="h-5 w-5" />
            </button>

            {/* Play/Pause Button */}
            <button
              onClick={(e) => { e.stopPropagation(); setIsPlaying(!isPlaying); }}
              className="absolute bottom-3 right-3 bg-white/80 hover:bg-white p-2 rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-opacity"
            >
              {isPlaying ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
            </button>

            {/* Dots Indicator */}
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-2">
              {media.map((_, index) => (
                <button
                  key={index}
                  onClick={(e) => { e.stopPropagation(); setCurrentIndex(index); }}
                  className={`w-2 h-2 rounded-full transition-all ${
                    index === currentIndex ? 'bg-white w-6' : 'bg-white/50'
                  }`}
                />
              ))}
            </div>
          </>
        )}

        {/* Caption */}
        {currentMedia.caption && (
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-4 opacity-0 group-hover:opacity-100 transition-opacity">
            <p className="text-white text-sm">{currentMedia.caption}</p>
          </div>
        )}

        {/* Media Counter */}
        {media.length > 1 && (
          <div className="absolute top-3 right-3 bg-black/50 text-white text-xs px-2 py-1 rounded-full">
            {currentIndex + 1} / {media.length}
          </div>
        )}

        {/* Video indicator */}
        {currentMedia.type === 'video' && (
          <div className="absolute top-3 right-14 bg-red-500 text-white text-xs px-2 py-1 rounded-full flex items-center gap-1">
            <Play className="h-3 w-3" /> Video
          </div>
        )}
      </div>

      {/* Description Box */}
      <div className={`bg-gradient-to-br ${getColorGradient(section.color_scheme)} rounded-2xl p-8`}>
        <h3 className="text-3xl font-bold mb-4 text-gray-900">{section.title}</h3>
        <p className="text-gray-700 leading-relaxed">
          {section.description}
        </p>
      </div>

      {/* Thumbnail Strip */}
      {media.length > 1 && (
        <div className="mt-4 flex gap-2 justify-center overflow-x-auto pb-2">
          {media.map((item, index) => (
            <button
              key={item.id || index}
              onClick={() => setCurrentIndex(index)}
              className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-all relative ${
                index === currentIndex ? 'border-green-500 scale-110' : 'border-transparent opacity-60 hover:opacity-100'
              }`}
            >
              {item.type === 'video' ? (
                <div className="w-full h-full bg-gray-800 flex items-center justify-center relative">
                  <video src={item.url} className="w-full h-full object-cover" muted />
                  <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                    <Play className="h-4 w-4 text-white" />
                  </div>
                </div>
              ) : (
                <img src={item.url} alt="" className="w-full h-full object-cover" />
              )}
            </button>
          ))}
        </div>
      )}

      {/* Lightbox Modal */}
      {showLightbox && (
        <MediaLightbox
          media={media}
          currentIndex={lightboxIndex}
          onClose={closeLightbox}
          onNext={lightboxNext}
          onPrevious={lightboxPrevious}
        />
      )}
    </div>
  );
};

const Ecosystem = () => {
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSections();
  }, []);

  // Handle hash navigation - scroll to section when hash changes or on load
  useEffect(() => {
    if (!loading && sections.length > 0) {
      const hash = window.location.hash.replace('#', '');
      if (hash) {
        // Small delay to ensure DOM is ready
        setTimeout(() => {
          const element = document.getElementById(hash);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            // Add highlight effect
            element.classList.add('ring-4', 'ring-green-400', 'ring-opacity-75');
            setTimeout(() => {
              element.classList.remove('ring-4', 'ring-green-400', 'ring-opacity-75');
            }, 2000);
          }
        }, 300);
      }
    }
  }, [loading, sections]);

  const fetchSections = async () => {
    try {
      const response = await axios.get(`${API}/ecosystem/sections`);
      setSections(response.data.sections || []);
    } catch (error) {
      console.error('Error fetching ecosystem sections:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="ecosystem-page">
      {/* Page Header */}
      <section className="page-header py-20 px-4 bg-gradient-to-b from-emerald-50 to-white">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
            Our Integrated Farming Ecosystem
          </h1>
          <p className="text-xl text-gray-600 leading-relaxed">
            At Daye Farm Ltd., we practice integrated farming where each component supports and enhances the others. Our cattle provide natural fertilizer for crops, cashew trees offer shade for mushroom cultivation, and our diverse crop selection ensures soil health throughout the year.
          </p>
        </div>
      </section>

      {/* Our Integrated System */}
      <section className="integrated-system-section py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl font-bold text-gray-900 mb-4 text-center">
            Our Integrated System
          </h2>
          <p className="text-xl text-gray-600 text-center mb-6 max-w-3xl mx-auto">
            At Daye Farm, every element works together in perfect harmony. Our livestock, crops, and trees form a sustainable cycle that nurtures the land and maximizes productivity.
          </p>
          <p className="text-center text-gray-500 mb-16">
            <span className="inline-flex items-center gap-2 bg-green-50 px-4 py-2 rounded-full text-green-700">
              <Maximize2 className="h-4 w-4" />
              Click any image to enlarge or play videos
            </span>
          </p>

          {/* Dynamic Sections with Carousels */}
          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-green-600"></div>
              <p className="mt-4 text-gray-600">Loading ecosystem...</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mb-16">
              {sections.map((section) => (
                <div 
                  key={section._id} 
                  id={section.name}
                  className="transition-all duration-500 rounded-2xl"
                >
                  <EcosystemCarousel section={section} />
                </div>
              ))}
            </div>
          )}

          {/* The Cycle Visualization */}
          <div className="bg-gradient-to-br from-green-600 to-emerald-700 rounded-3xl p-12 text-white">
            <h3 className="text-3xl font-bold text-center mb-8">The Natural Cycle</h3>
            <div className="flex flex-col md:flex-row items-center justify-center gap-8">
              <div className="text-center">
                <div className="w-32 h-32 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center mb-4 mx-auto border-4 border-white/30">
                  <span className="text-5xl">🐄</span>
                </div>
                <p className="font-semibold text-lg">Livestock provides nutrients</p>
              </div>
              <div className="text-4xl">→</div>
              <div className="text-center">
                <div className="w-32 h-32 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center mb-4 mx-auto border-4 border-white/30">
                  <span className="text-5xl">🌾</span>
                </div>
                <p className="font-semibold text-lg">Crops grow abundantly</p>
              </div>
              <div className="text-4xl">→</div>
              <div className="text-center">
                <div className="w-32 h-32 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center mb-4 mx-auto border-4 border-white/30">
                  <span className="text-5xl">🌳</span>
                </div>
                <p className="font-semibold text-lg">Trees create microclimate</p>
              </div>
              <div className="text-4xl">→</div>
              <div className="text-center">
                <div className="w-32 h-32 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center mb-4 mx-auto border-4 border-white/30">
                  <span className="text-5xl">🔄</span>
                </div>
                <p className="font-semibold text-lg">Cycle continues</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Ecosystem Benefits - Storytelling with Tropical Farm */}
      <section className="benefits-storytelling py-20 px-4 bg-gradient-to-b from-green-50 to-white">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl font-bold text-gray-900 mb-16 text-center">
            The Benefits of Our Tropical Farmland
          </h2>

          {/* Benefit 1 - Image Left */}
          <div className="grid md:grid-cols-2 gap-12 items-center mb-20">
            <div className="h-96 rounded-2xl overflow-hidden shadow-2xl">
              <img 
                src="https://images.unsplash.com/photo-1500382017468-9049fed747ef" 
                alt="Natural nutrient cycling" 
                className="w-full h-full object-cover"
              />
            </div>
            <div>
              <h3 className="text-3xl font-bold mb-6 text-gray-900">Natural Nutrient Cycling</h3>
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                In the heart of our tropical farmland, nature's wisdom guides our every practice. Our cattle roam freely, their natural waste becoming gold for our soil. This organic matter enriches the earth, feeding the microorganisms that make nutrients available to our crops.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                Unlike chemical fertilizers that deplete soil over time, our natural cycle builds soil health year after year. The result? Crops that are more nutritious, more flavorful, and grown in harmony with nature.
              </p>
            </div>
          </div>

          {/* Benefit 2 - Image Right */}
          <div className="grid md:grid-cols-2 gap-12 items-center mb-20">
            <div className="order-2 md:order-1">
              <h3 className="text-3xl font-bold mb-6 text-gray-900">Reduced Environmental Impact</h3>
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                Our tropical farm operates on a simple principle: take nothing from the earth that we cannot return. Every action is measured against its impact on the environment. By integrating multiple farming systems, we minimize waste and maximize efficiency.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                Water that nourishes our crops seeps into the ground, replenishing aquifers. Trees planted throughout the farm capture carbon and provide habitat for beneficial insects. Nothing goes to waste—crop residues feed livestock, livestock manure feeds crops.
              </p>
            </div>
            <div className="h-96 rounded-2xl overflow-hidden shadow-2xl order-1 md:order-2">
              <img 
                src="https://images.unsplash.com/photo-1464226184884-fa280b87c399" 
                alt="Sustainable practices" 
                className="w-full h-full object-cover"
              />
            </div>
          </div>

          {/* Benefit 3 - Image Left */}
          <div className="grid md:grid-cols-2 gap-12 items-center mb-20">
            <div className="h-96 rounded-2xl overflow-hidden shadow-2xl">
              <img 
                src="https://images.unsplash.com/photo-1560493676-04071c5f467b" 
                alt="Improved soil health" 
                className="w-full h-full object-cover"
              />
            </div>
            <div>
              <h3 className="text-3xl font-bold mb-6 text-gray-900">Building Soil Health</h3>
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                Beneath the surface of our tropical farmland lies a thriving ecosystem. Rich, dark soil teeming with life—earthworms, beneficial bacteria, fungi—all working together to create the perfect growing medium.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                This isn't achieved overnight. It's the result of years of careful stewardship, of understanding that healthy soil is the foundation of everything we do. Each season, our soil becomes richer, more resilient, more productive.
              </p>
            </div>
          </div>

          {/* Benefit 4 - Image Right */}
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="order-2 md:order-1">
              <h3 className="text-3xl font-bold mb-6 text-gray-900">Year-Round Productivity</h3>
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                The tropical climate blesses us with extended growing seasons and abundant sunshine. But we don't just rely on nature's generosity—we enhance it through smart planning and diversification.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                While cassava matures in one section, yams are being planted in another. Cashew trees provide shade year-round for mushroom cultivation. Our farm never sleeps, never lies fallow unnecessarily. Every day, something is growing, something is being harvested, something is returning to the soil.
              </p>
            </div>
            <div className="h-96 rounded-2xl overflow-hidden shadow-2xl order-1 md:order-2">
              <img 
                src="https://images.unsplash.com/photo-1495107334309-fcf20504a5ab" 
                alt="Year-round farming" 
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Together - With Images */}
      <section className="integration-section py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl font-bold text-gray-900 mb-4 text-center">
            How It Works Together
          </h2>
          <p className="text-xl text-gray-600 text-center mb-16 max-w-3xl mx-auto">
            Each element of our farm plays a vital role in the greater ecosystem. Here's how they interconnect to create a thriving, sustainable operation.
          </p>

          <div className="grid md:grid-cols-2 gap-8">
            <Card className="hover:shadow-2xl transition-all duration-300 overflow-hidden group">
              <div className="h-64 overflow-hidden">
                <img 
                  src="https://images.unsplash.com/photo-1500595046743-cd271d694d30" 
                  alt="Nutrient cycling" 
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
              </div>
              <CardContent className="p-8">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-amber-100 rounded-full mb-6">
                  <Recycle className="h-8 w-8 text-amber-700" />
                </div>
                <h3 className="text-2xl font-bold mb-4 text-gray-900">Nutrient Cycling</h3>
                <p className="text-gray-700 leading-relaxed">
                  Cattle provide natural fertilizer that enriches the soil for our crops. Crop residues and farm waste are composted and returned to the land, creating a closed-loop system where nothing is wasted.
                </p>
              </CardContent>
            </Card>

            <Card className="hover:shadow-2xl transition-all duration-300 overflow-hidden group">
              <div className="h-64 overflow-hidden">
                <img 
                  src="https://images.unsplash.com/photo-1523348837708-15d4a09cfac2" 
                  alt="Water management" 
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
              </div>
              <CardContent className="p-8">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-6">
                  <Droplets className="h-8 w-8 text-blue-700" />
                </div>
                <h3 className="text-2xl font-bold mb-4 text-gray-900">Water Management</h3>
                <p className="text-gray-700 leading-relaxed">
                  Strategic placement of crops and trees helps retain soil moisture. Our cashew trees provide shade that reduces water evaporation and creates ideal conditions for mushroom growth beneath their canopy.
                </p>
              </CardContent>
            </Card>

            <Card className="hover:shadow-2xl transition-all duration-300 overflow-hidden group">
              <div className="h-64 overflow-hidden">
                <img 
                  src="https://images.unsplash.com/photo-1574943320219-553eb213f72d" 
                  alt="Light optimization" 
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
              </div>
              <CardContent className="p-8">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-yellow-100 rounded-full mb-6">
                  <Sun className="h-8 w-8 text-yellow-700" />
                </div>
                <h3 className="text-2xl font-bold mb-4 text-gray-900">Light Optimization</h3>
                <p className="text-gray-700 leading-relaxed">
                  Multi-layered farming maximizes land use. Cashew trees provide partial shade for mushroom cultivation while allowing sufficient sunlight for understory crops, making the most of every ray of tropical sunshine.
                </p>
              </CardContent>
            </Card>

            <Card className="hover:shadow-2xl transition-all duration-300 overflow-hidden group">
              <div className="h-64 overflow-hidden">
                <img 
                  src="https://images.unsplash.com/photo-1625246333195-78d9c38ad449" 
                  alt="Soil health" 
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
              </div>
              <CardContent className="p-8">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-6">
                  <Wind className="h-8 w-8 text-green-700" />
                </div>
                <h3 className="text-2xl font-bold mb-4 text-gray-900">Soil Health</h3>
                <p className="text-gray-700 leading-relaxed">
                  Diverse crop rotation and integration of livestock grazing prevent soil depletion. Tree roots stabilize the soil and improve its structure and fertility over time, building a foundation for generations.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Ecosystem;
